import { axiosAiq } from 'config';
import { notificationHandlerSync } from 'utils/utils';
import { PTZ_REQUEST } from './config';

const { STATUS } = PTZ_REQUEST;

export class PTZApiClient {
    #cameraId;
    #maxRetries;
    #onStatusChange;
    #requestInProgress;
    #statusTimeoutId = null;

    constructor(cameraId, { maxRetries = 3, onStatusChange = null } = {}) {
        this.#cameraId = cameraId;
        this.#maxRetries = maxRetries;
        this.#onStatusChange = onStatusChange;
    }

    async #withRetry(asyncFn, statusMessage, errorTitle, { shouldNotRetry } = {}, tooltipMessage = '') {
        if (this.#requestInProgress) return;

        let lastError;

        for (let attempt = 0; attempt < this.#maxRetries; attempt++) {
            try {
                this.#setRequestStatus(STATUS.IN_PROGRESS, statusMessage, tooltipMessage);
                await asyncFn();
                this.#setRequestStatus(STATUS.SUCCESS);
                return;
            } catch (err) {
                lastError = err;
                if (shouldNotRetry?.(err)) break;
            }
        }
        this.#setRequestStatus(STATUS.ERROR);
        notificationHandlerSync({ err: lastError, title: errorTitle });
    }

    async sendRegionRequest({ x1, y1, x2, y2, streamWidth, streamHeight }) {
        const payload = {
            x1,
            y1,
            x2,
            y2,
            streamWidth,
            streamHeight,
            cameraId: this.#cameraId,
        };

        return this.#withRetry(
            () => axiosAiq.put(PTZ_REQUEST.PATH.PTZ_REGION(), payload),
            `Zooming ${x2 > x1 ? 'in' : 'out'}...`,
            'Error sending PTZ region request'
        );
    }

    async sendRecenterRequest({ centerX, centerY, streamWidth, streamHeight }) {
        const payload = {
            centerX,
            centerY,
            streamWidth,
            streamHeight,
            cameraId: this.#cameraId,
        };

        return this.#withRetry(() => axiosAiq.put(PTZ_REQUEST.PATH.PTZ_RECENTER(), payload), 'Recentering...', 'Error sending PTZ recenter request');
    }

    async goToPreset(presetKey) {
        return this.#withRetry(
            () => axiosAiq.put(PTZ_REQUEST.PATH.PTZ_PRESET(this.#cameraId, presetKey)),
            `Moving to preset ${presetKey}...`,
            `Preset ${presetKey} is not available. Please select a different preset`,
            { shouldNotRetry: (err) => err.response?.status === 404 },
            `Preset (${presetKey}) – may take over 3s`
        );
    }

    async sendFullZoomIn() {
        return this.#withRetry(
            () => axiosAiq.put(PTZ_REQUEST.PATH.PTZ_FULL_ZOOM_IN(this.#cameraId)),
            'Zooming fully in...',
            'Error sending full zoom in request'
        );
    }

    async sendFullZoomOut() {
        return this.#withRetry(
            () => axiosAiq.put(PTZ_REQUEST.PATH.PTZ_FULL_ZOOM_OUT(this.#cameraId)),
            'Zooming fully out...',
            'Error sending full zoom out request'
        );
    }

    #setRequestStatus(status, message = '', tooltipMessage = '') {
        this.#clearStatusTimeout();
        this.#requestInProgress = status === STATUS.IN_PROGRESS;
        this.#onStatusChange?.((state) => {
            const { status: oldStatus, message: oldMessage, tooltipMessage: oldTooltipMessage } = state;
            if (oldStatus === status && oldMessage === message && oldTooltipMessage === tooltipMessage) return state;
            return { status, message, tooltipMessage };
        });

        if (status !== STATUS.IN_PROGRESS) {
            const timeout = PTZ_REQUEST.NOTIFICATION_TIMEOUT_MS.RESULT;
            this.#statusTimeoutId = setTimeout(() => {
                this.#onStatusChange?.((state) => {
                    if (state.status === null) return state;
                    return { status: null, message: '', tooltipMessage: '' };
                });
            }, timeout);
        }
    }

    #clearStatusTimeout() {
        if (this.#statusTimeoutId) {
            clearTimeout(this.#statusTimeoutId);
            this.#statusTimeoutId = null;
        }
    }

    destroy() {
        this.#clearStatusTimeout();
        this.#requestInProgress = false;
    }
}
